import { ref, watch, computed } from "vue";
import { defineStore } from "pinia";
import i18n from "@/i18n";
import { ModuleManager } from "@module-system";
import { Toast, userMediaErrorT } from "@/utils";

export const useDeviceState = defineStore("device-state", () => {
  const cameraPermission = ref(null);
  const microphonePermission = ref(null);
  const cameraPermissionState = ref(null);
  const microphonePermissionState = ref(null);
  let _readyResolve = null;
  const ready = new Promise((resolve) => {
    _readyResolve = resolve;
  });

  (async () => {
    cameraPermission.value = await navigator.permissions.query({
      name: "camera",
    });
    microphonePermission.value = await navigator.permissions.query({
      name: "microphone",
    });

    cameraPermissionState.value = cameraPermission.value.state;
    microphonePermissionState.value = microphonePermission.value.state;

    _readyResolve();
  })();

  // This function/computed is not working properly on iOS Safari, when used in onMounted
  // So try not to use it in onMounted
  const errors = computed(() => {
    let errors = [];

    if (cameraPermission.value == null || microphonePermission.value == null) {
      return errors;
    }

    // add NoCameraPermission and NoMicrophonePermission to errors array if camera or microphone permission is denied
    if (cameraPermissionState.value === "denied") {
      errors.push(i18n.global.t("NoCameraPermission"));
    }

    if (microphonePermissionState.value === "denied") {
      errors.push(i18n.global.t("NoMicrophonePermission"));
    }
    return errors;
  });

  // This function/computed is not working properly on iOS Safari, when used in onMounted
  // So try not to use it in onMounted
  const canUseCamera = computed(() => {
    return cameraPermissionState.value === "granted";
  });

  async function checkPermissions() {
    cameraPermission.value.onchange = () => {
      console.log("camera permission changed to " + cameraPermission.value.state);
      cameraPermissionState.value = cameraPermission.value.state;
    };

    microphonePermission.value.onchange = () => {
      console.log("microphone permission changed to " + microphonePermission.value.state);
      microphonePermissionState.value = microphonePermission.value.state;
    };

    console.log("camera permission is " + cameraPermission.value.state);
    console.log("microphone permission is " + microphonePermission.value.state);

    if (cameraPermission.value.state === "prompt" || microphonePermission.value.state === "prompt") {
      console.log("prompting permission");
      await getUserMedia();
    }
  }

  const cameraId = ref("");
  const microphoneId = ref("");

  // Background replacement/blur on the agent-preview page. OFF by default: preview exists to keep the
  // camera warm (holding the raw stream), and running a 2nd pipeline alongside the call tanks the PC.
  // Agents opt in per-session via a checkbox on the preview page; the bg hook reads this for no-call media.
  const previewBackgroundEnabled = ref(false);

  (async () => {
    cameraId.value = (await localStorageCustom.getItem("ecv-cameraId")) || "";
    microphoneId.value = (await localStorageCustom.getItem("ecv-microphoneId")) || "";
  })();

  async function setCameraId(id) {
    if (id) await window.localStorageCustom.setItem("ecv-cameraId", id);
    else await window.localStorageCustom.removeItem("ecv-cameraId");

    console.log("setCameraId", id);
    cameraId.value = id;
  }

  async function setMicrophoneId(id) {
    if (id) await window.localStorageCustom.setItem("ecv-microphoneId", id);
    else await window.localStorageCustom.removeItem("ecv-microphoneId");

    console.log("setMicrophoneId", id);
    microphoneId.value = id;
  }

  function getUserMediaFunction() {
    return navigator.mediaDevices?.getUserMedia;
  }

  function relaxConstraintsDeviceId(constraints) {
    console.log("relaxConstraints DeviceId called");
    var copy = JSON.parse(JSON.stringify(constraints));
    constraints = copy;
    if (constraints.video && constraints.video.deviceId) {
      delete constraints.video.deviceId;
      setCameraId(null);
    }
    if (constraints.audio && constraints.audio.deviceId) {
      delete constraints.audio.deviceId;
      setMicrophoneId(null);
    }
    return constraints;
  }

  // remove all constraints for audio simply set audio to true
  function relaxConstraintsAudio(constraints) {
    console.log("relaxConstraints Audio simplify called");
    var copy = JSON.parse(JSON.stringify(constraints));
    constraints = copy;

    if (constraints.audio) constraints.audio = true;
    return constraints;
  }

  function relaxConstraintsVideoFrameRate(constraints) {
    console.log("relaxConstraints VideoFrameRate called");
    var copy = JSON.parse(JSON.stringify(constraints));
    constraints = copy;

    if (constraints.video && constraints.video.frameRate) {
      delete constraints.video.frameRate;
    }
    return constraints;
  }

  function relaxConstraintsVideoResolutionLower(constraints) {
    console.log("relaxConstraints VideoResolution Lower called");

    // if already ideal width is 640 or lower, then remove width and height constraints
    if (
      constraints.video &&
      typeof constraints.video === "object" &&
      constraints.video.width &&
      constraints.video.width.ideal &&
      constraints.video.width.ideal <= 640
    ) {
      console.log("Video resolution is already ideal, removing width/height constraints");
      return relaxConstraintsVideoResolutionRemove(constraints);
    }

    var copy = JSON.parse(JSON.stringify(constraints));
    constraints = copy;

    if (constraints.video && typeof constraints.video === "object") {
      constraints.video = { ...constraints.video, width: { ideal: 640 }, height: { ideal: 480 } };
    }
    return constraints;
  }

  function relaxConstraintsVideoResolutionRemove(constraints) {
    console.log("relaxConstraints VideoResolution Remove called");
    var copy = JSON.parse(JSON.stringify(constraints));
    constraints = copy;

    if (constraints.video && typeof constraints.video === "object") {
      constraints.video = true;
    }
    return constraints;
  }

  function relaxToDefaultConstraints(constraints) {
    console.log("relaxToDefaultConstraints called, setting audio and video to true");
    return { video: true, audio: true };
  }

  function cleanConstraints(constraints) {
    var copy = JSON.parse(JSON.stringify(constraints));
    constraints = copy;

    if (constraints.video && typeof constraints.video === "object") {
      // if no property exists in constraints.video, set video to true
      if (Object.keys(constraints.video).length === 0) {
        constraints.video = true;
      }
    }

    if (constraints.audio && typeof constraints.audio === "object") {
      // if no property exists in constraints.audio, set audio to true
      if (Object.keys(constraints.audio).length === 0) {
        constraints.audio = true;
      }
    }

    return constraints;
  }

  async function getUserMedia(constraints, rtcId = null, fromExistingMediaRef = null) {
    cameraId.value = (await localStorageCustom.getItem("ecv-cameraId")) || "";
    microphoneId.value = (await localStorageCustom.getItem("ecv-microphoneId")) || "";

    if (!constraints) {
      constraints = {};
    }

    // Default to on only when unspecified — an explicit `false` (disableVideo/disableAudio, e.g. the
    // audio-only supervisor whisper) must be respected, or it acquires the camera and spins up a 2nd
    // bg pipeline that collides with the main call on the shared source <video>.
    if (constraints.video === undefined || constraints.video === null) {
      constraints.video = true;
    }

    if (constraints.audio === undefined || constraints.audio === null) {
      constraints.audio = true;
    }

    if (cameraId.value && constraints.video !== false) {
      constraints.video = {
        deviceId: {
          exact: cameraId.value,
        },
        ...constraints.video,
      };
    }

    if (microphoneId.value && constraints.audio !== false) {
      constraints.audio = {
        deviceId: {
          exact: microphoneId.value,
        },
        ...constraints.audio,
      };
    }

    console.log("getUserMedia constraints", constraints);
    let media = fromExistingMediaRef ? fromExistingMediaRef : ref(null);

    var constraintsRelaxers = [
      relaxConstraintsDeviceId,
      relaxConstraintsAudio,
      relaxConstraintsVideoFrameRate,
      relaxConstraintsVideoResolutionLower,
      relaxConstraintsVideoResolutionRemove,
      relaxToDefaultConstraints,
    ];
    let relaxerIndex = 0;

    let alreadyTriedDeviceIdRelaxationList = [];

    let tryGetMedia = null;

    while (true) {
      try {
        constraints = cleanConstraints(constraints);
        tryGetMedia = await navigator.mediaDevices.getUserMedia(constraints);
        break; // success
      } catch (error) {
        console.error("getUserMedia error - constraints", error);
        console.error("constraints resaon:", error.constraint);

        if (
          error.constraint &&
          error.constraint === "deviceId" &&
          !alreadyTriedDeviceIdRelaxationList.includes(relaxConstraintsDeviceId)
        ) {
          console.warn("DeviceId constraint caused error, removing deviceId constraints");
          constraints = relaxConstraintsDeviceId(constraints);
          alreadyTriedDeviceIdRelaxationList.push(relaxConstraintsDeviceId);
          continue;
        }

        if (
          error.constraint &&
          error.constraint === "audio" &&
          !alreadyTriedDeviceIdRelaxationList.includes(relaxConstraintsAudio)
        ) {
          console.warn("Audio constraint caused error, removing audio constraints");
          constraints = relaxConstraintsAudio(constraints);
          alreadyTriedDeviceIdRelaxationList.push(relaxConstraintsAudio);
          continue;
        }

        if (
          error.constraint &&
          (error.constraint === "width" || error.constraint === "height") &&
          (!alreadyTriedDeviceIdRelaxationList.includes(relaxConstraintsVideoResolutionLower) ||
            !alreadyTriedDeviceIdRelaxationList.includes(relaxConstraintsVideoResolutionRemove))
        ) {
          console.warn("Video resolution constraint caused error, removing width/height constraints");
          constraints = relaxConstraintsVideoResolutionLower(constraints);
          alreadyTriedDeviceIdRelaxationList.push(relaxConstraintsVideoResolutionLower);
          continue;
        }

        if (
          error.constraint &&
          error.constraint === "frameRate" &&
          !alreadyTriedDeviceIdRelaxationList.includes(relaxConstraintsVideoFrameRate)
        ) {
          console.warn("Video frameRate constraint caused error, removing frameRate constraints");
          constraints = relaxConstraintsVideoFrameRate(constraints);
          alreadyTriedDeviceIdRelaxationList.push(relaxConstraintsVideoFrameRate);
          continue;
        }

        let appliedNewConstraint = false;
        // if OverconstrainedError then relax the constraints and try again
        if (error.name === "OverconstrainedError" || error.name === "ConstraintNotSatisfiedError") {
          if (relaxerIndex < constraintsRelaxers.length) {
            console.warn("OverconstrainedError, trying again with relaxed constraints", error);
            constraints = constraintsRelaxers[relaxerIndex](constraints);
            relaxerIndex++;
            console.log("new constraints", constraints);
            appliedNewConstraint = true;
          }
        }

        if (error.name === "NotReadableError" || error.name === "TrackStartError") {
          // sometimes NotReadableError is caused by too high resolution, so try to relax video resolution constraints
          if (relaxerIndex < constraintsRelaxers.length) {
            console.warn("NotReadableError, trying again with relaxed video resolution constraints", error);
            constraints = constraintsRelaxers[relaxerIndex](constraints);
            relaxerIndex++;
            console.log("new constraints", constraints);
            appliedNewConstraint = true;
          }
        }

        // add delay before next try
        console.log("waiting 100ms before next getUserMedia try");
        await new Promise((resolve) => setTimeout(resolve, 100));
        console.log("getUserMedia waiting done");

        if (!appliedNewConstraint) {
          Toast.fire({
            icon: "error",
            title: i18n.global.t("Error"),
            text: userMediaErrorT(error),
          });
          throw error;
        }
      }
    }

    let resetMedia = ref(0);

    async function applyModules(applyToMedia, rtcId) {
      const mods = ModuleManager.getHookModules("modifyUserMedia");

      for (const module of mods) {
        try {
          const newMedia = await module.run({
            media: applyToMedia,
            rtcId,
            resetMedia: () => {
              resetMedia.value++;
            },
          });
          if (newMedia) {
            applyToMedia = newMedia;
          }
        } catch (e) {
          console.error("error in ", module.location, "module", module.name, "priority", module.priotiy, module.description);
          console.error(e);
        }
      }
      return applyToMedia;
    }

    if (!fromExistingMediaRef) {
      watch(cameraId, async (newVal, oldVal) => {
        if (constraints?.video?.deviceId === newVal) {
          return;
        }

        constraints = {
          ...constraints,
          video: {
            ...constraints.video,
            deviceId: {
              exact: newVal,
            },
          },
        };

        console.log("constraints changed due to cameraId change", constraints);

        try {
          await getUserMedia(constraints, rtcId, media);
        } catch (error) {
          console.error("cameraId change error", error);
          cameraId.value = oldVal;

          Toast.fire({
            icon: "error",
            title: i18n.global.t("Error"),
            text: userMediaErrorT(error),
          });
        }
      });

      watch(microphoneId, async (newVal, oldVal) => {
        if (constraints?.audio?.deviceId === newVal) {
          return;
        }

        constraints = {
          ...constraints,
          audio: {
            ...constraints.audio,
            deviceId: {
              exact: newVal,
            },
          },
        };

        console.log("constraints changed due to microphoneId change", constraints);

        try {
          // store old media
          await getUserMedia(constraints, rtcId, media);
        } catch (error) {
          console.error("microphoneId change error", error);
          microphoneId.value = oldVal;

          Toast.fire({
            icon: "error",
            title: i18n.global.t("Error"),
            text: userMediaErrorT(error),
          });
        }
      });

      watch(resetMedia, async () => {
        console.log("resetMedia triggered, reacquiring media with current constraints", constraints);
        try {
          await getUserMedia(constraints, rtcId, media);
        } catch (error) {
          console.error("resetMedia error", error);

          Toast.fire({
            icon: "error",
            title: i18n.global.t("Error"),
            text: userMediaErrorT(error),
          });
        }
      });
    }
    media.value = await applyModules(tryGetMedia, rtcId);
    media.value.getTracks().forEach((track) => {
      console.log("getConstraints result for " + track.kind, track.getConstraints());
      console.log("getSettings result for " + track.kind, track.getSettings());
    });
    return media;
  }

  return {
    cameraId,
    microphoneId,
    previewBackgroundEnabled,
    setCameraId,
    setMicrophoneId,
    getUserMedia,
    getUserMediaFunction,
    checkPermissions,
    errors,
    canUseCamera,
    cameraPermission,
    microphonePermission,
    ready,
  };
});
